import { type JSX, type ComponentProps, useMemo } from 'react'
import { CardActivityFeedRow } from './CardActivityFeedRow'
import { Loading } from '@/src/common/components/Loading'
import { useGetCardActivityId, type CardActivity, type CardActivityId } from './CardActivity'
import { useCardActivityFeed } from './useCardActivityFeed'
import { BottomSheetSectionList } from '@gorhom/bottom-sheet'
import { CardActivityFeedBottomSheet } from './CardActivityFeedBottomSheet'
import Animated, { type SharedValue } from 'react-native-reanimated'
import { sortByTimeDescending } from '@/src/common/utils/date'
import {
  type LegacyScheduledTransferActivity,
  useLegacyScheduledTransfers,
} from './ScheduledTransfers/useLegacyScheduledTransfers'
import { LegacyScheduledTransferRow } from './ScheduledTransfers/LegacyScheduledTransferRow'
import { CardActivityFeedEmpty } from './CardActivityFeedEmpty'
import { randomUUID } from '@/src/common/utils/randomUUID'
import { Platform, StyleSheet, Text } from 'react-native'
import { TopLevelHeader, transactionsTopLevelHeader } from './TopLevelHeader'
import { CardPaymentsE2E } from '@/src/experiences/card-payments/e2e'
import { mesaColorNames, spacing, typography } from '@/src/common/theme'
import { useRefreshPoints } from '@/src/common/hooks/usePoints'
import { CardActivityFeedProps } from '../feed'

/** Example: December 2024 */
function getMonthSectionTitle(cardActivity: CardActivity) {
  return new Date(cardActivity.sourceCreatedAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
  })
}

function sortCardActivitiesByTimeDescending(a: CardActivity, b: CardActivity) {
  return sortByTimeDescending(a.sourceCreatedAt, b.sourceCreatedAt)
}

type Section = (ComponentProps<
  typeof BottomSheetSectionList<LegacyScheduledTransferActivity | CardActivity>
>['sections'] extends infer T | SharedValue<infer T>
  ? T
  : never)[number]

const SCHEDULED_PAYMENTS_MARKER = 'SCHEDULED PAYMENTS'

/**
 * we have >2 levels of sections:
 * - ScheduledTransfers
 * - CardActivity
 *   - Pending
 *   - Posted
 *     - Month
 *
 * using this type allows us to render an H3 header for each top-level section
 */
interface SectionWithTopLevelHeader extends Section {
  topLevelHeader: JSX.Element
}

export function Feed({
  children,
  cardActivityFeedBottomSheetHeightStore,
  onRefresh,
}: CardActivityFeedProps) {
  const {
    pendingActivities,
    postedActivities,
    loadingPendingActivities,
    loadingPostedActivities,
    fetchOlderPostedActivities,
    fetchNewerPostedActivities,
  } = useCardActivityFeed()

  const scheduledTransfersQuery = useLegacyScheduledTransfers()

  const refreshPoints = useRefreshPoints()

  const getCardActivityId = useGetCardActivityId()

  const scheduledTransfersSection = useMemo(() => {
    if (!scheduledTransfersQuery.data) return undefined
    return {
      title: SCHEDULED_PAYMENTS_MARKER,
      data: scheduledTransfersQuery.data,
      topLevelHeader: (
        <TopLevelHeader testID={CardPaymentsE2E.Ids.Text.ScheduledPaymentsHeader}>
          Scheduled Payments
        </TopLevelHeader>
      ),
    } as const satisfies SectionWithTopLevelHeader
  }, [scheduledTransfersQuery.data])

  const pendingActivitiesSection = useMemo(() => {
    if (!pendingActivities) return undefined

    const dedupedPendingActivities = Array.from(
      pendingActivities
        .reduce(
          (acc, activity) => acc.set(getCardActivityId(activity), activity),
          new Map<CardActivityId, CardActivity>(),
        )
        .values(),
    )

    return {
      title: 'PENDING',
      data: dedupedPendingActivities.toSorted(sortCardActivitiesByTimeDescending),
    } as const satisfies Section
  }, [pendingActivities, getCardActivityId])

  const postedActivitiesSections = useMemo(() => {
    if (!postedActivities) return undefined

    const dedupedPostedActivities = postedActivities.reduce(
      (acc, activity) => acc.set(getCardActivityId(activity), activity),
      new Map<CardActivityId, CardActivity>(),
    )

    const sortedPostedActivities = Array.from(dedupedPostedActivities.values()).toSorted(
      sortCardActivitiesByTimeDescending,
    )

    const postedActivitiesGroupedByMonth = Object.groupBy(
      sortedPostedActivities,
      getMonthSectionTitle,
    )

    return Object.entries(postedActivitiesGroupedByMonth)
      .filter(([_, data]) => data?.length)
      .toSorted(([monthA], [monthB]) => sortByTimeDescending(monthA, monthB))
      .map(([title, data = []]) => ({ title, data }) satisfies Section)
  }, [postedActivities, getCardActivityId])

  const sectionList = (() => {
    if (!scheduledTransfersSection || !pendingActivitiesSection || !postedActivitiesSections) {
      return <Loading />
    }

    const scheduledTransfersSections = scheduledTransfersSection.data.length
      ? ([scheduledTransfersSection] as const satisfies SectionWithTopLevelHeader[])
      : []
    const pendingSections = pendingActivitiesSection.data.length ? [pendingActivitiesSection] : []
    const postedSections = postedActivitiesSections.length ? postedActivitiesSections : []
    const [firstCardActivitySection, ...restCardActivitySections] = [
      ...pendingSections,
      ...postedSections,
    ] as const satisfies Section[]
    const firstCardActivitySectionWithTopLevelHeader = firstCardActivitySection
      ? ({
          ...firstCardActivitySection,
          topLevelHeader: transactionsTopLevelHeader,
        } as const satisfies SectionWithTopLevelHeader)
      : undefined
    const cardActivitySections = [
      ...(firstCardActivitySectionWithTopLevelHeader
        ? [firstCardActivitySectionWithTopLevelHeader]
        : []),
      ...restCardActivitySections,
    ]

    const sections = [...scheduledTransfersSections, ...cardActivitySections].map(
      (section) =>
        ({
          ...section,
          /** there's a bizarre type-level issue i'm too lazy to understand right now. for some reason this fixes it */
          data: section.data.map((item) => item),
        }) as const,
    ) satisfies Section[]

    return (
      <BottomSheetSectionList
        sections={sections}
        contentContainerStyle={styles.infiniteScrollStyles}
        refreshing={loadingPendingActivities || loadingPostedActivities}
        onRefresh={() => {
          onRefresh?.()
          void refreshPoints()
          void scheduledTransfersQuery.refetch()
          fetchNewerPostedActivities()
        }}
        onEndReached={fetchOlderPostedActivities}
        keyExtractor={(item) => {
          switch (item.__typename) {
            case 'CardActivityStatementCredit':
            case 'CardActivityCardPayment':
            case 'CardActivityTransactionEvent':
            case 'CardActivityFinancialEvent':
              return getCardActivityId(item)
            case 'OneTimeAchTransferResponse':
            case 'RecurringAchTransferResponse':
              return item.id
            default:
              // if this check fails, we haven't handled every possible type of item
              item satisfies never
              return randomUUID()
          }
        }}
        stickySectionHeadersEnabled={
          // the browser's implementation of sticky section headers is a little different such that we need the right combination of background colors and z-indices (in `CellRendererComponent`). i don't have enough time to fix this before the web GA launch
          Platform.OS !== 'web'
        }
        onEndReachedThreshold={0.1}
        renderSectionHeader={({ section }) => {
          const topLevelHeader = 'topLevelHeader' in section ? section.topLevelHeader : null
          return (
            <>
              {topLevelHeader}
              {topLevelHeader === scheduledTransfersSection.topLevelHeader ? null : (
                <Text style={[styles.sectionHeader, { paddingHorizontal: spacing['4'] }]}>
                  {section.title}
                </Text>
              )}
            </>
          )
        }}
        renderItem={({ item, index }) => {
          switch (item.__typename) {
            case 'CardActivityStatementCredit':
            case 'CardActivityCardPayment':
            case 'CardActivityTransactionEvent':
            case 'CardActivityFinancialEvent':
              return <CardActivityFeedRow cardActivity={item} />
            case 'OneTimeAchTransferResponse':
            case 'RecurringAchTransferResponse':
              return <LegacyScheduledTransferRow {...item} index={index} />
            default:
              // if this check fails, we haven't handled every possible type of item
              item satisfies never
              return null
          }
        }}
        testID="bottom_sheet"
        ListEmptyComponent={() => (
          <CardActivityFeedEmpty
            cardActivityFeedBottomSheetHeightStore={cardActivityFeedBottomSheetHeightStore}
          />
        )}
      />
    )
  })()

  return (
    <>
      {children}
      <CardActivityFeedBottomSheet
        cardActivityFeedBottomSheetHeightStore={cardActivityFeedBottomSheetHeightStore}
      >
        <Animated.View style={cardActivityFeedBottomSheetHeightStore.animatedStyles}>
          {sectionList}
        </Animated.View>
      </CardActivityFeedBottomSheet>
    </>
  )
}

const styles = StyleSheet.create({
  infiniteScrollStyles: {
    /** some extra padding on the bottom to derisk the last row being covered by the bottom nav bar */
    paddingBottom: 88,
  },
  sectionHeader: {
    backgroundColor: mesaColorNames['Mesa - Brand/White #FFFFFF'],
    color: mesaColorNames['semantic/fg/subtle #898D8F'],
    ...typography['heading/heading6'],
    marginBottom: spacing.base - 10,
  },
})
